/**
 * Layers tab — list and create/delete custom map layers (GeoJSON payload).
 *
 * Payload is a free-form JSON object — typically a GeoJSON FeatureCollection
 * but anything ≤1 MB is accepted by shuttle-svc.
 *
 * @module panels/shuttle/layers
 */
import * as api from "../../api/shuttle.ts";
import type { TabContext } from "./index.ts";

export async function renderLayersTab(
	root: HTMLElement,
	ctx: TabContext,
): Promise<void> {
	const layers = ctx.data.layers;

	root.innerHTML = `
    <div class="shuttle-grid-2">
      <div>
        <div class="panel-header"><h3>Map layers (${layers.length})</h3></div>
        <table class="shuttle-table">
          <thead><tr><th>Name</th><th>Kind</th><th>Z</th><th></th></tr></thead>
          <tbody>
            ${layers
							.map(
								(l) => `
              <tr>
                <td>${escapeHtml(l.name)}</td>
                <td>${escapeHtml(l.kind)}</td>
                <td class="num">${l.z_index ?? 0}</td>
                <td><button class="shuttle-btn danger" data-del="${l.id}">Delete</button></td>
              </tr>
            `,
							)
							.join("")}
          </tbody>
        </table>
      </div>

      <div>
        <div class="panel-header"><h3>New layer</h3></div>
        <div class="shuttle-form">
          <label>Name<input id="ly-name" type="text" /></label>
          <label>Kind
            <select id="ly-kind" class="shuttle-select">
              <option value="geojson">geojson</option>
              <option value="overlay">overlay</option>
              <option value="poi">poi</option>
            </select>
          </label>
          <label>Z-index<input id="ly-z" type="number" value="10" /></label>
          <label>Style (JSON, optional)
            <textarea id="ly-style" rows="3" placeholder='{"color":"#ff0000"}'></textarea>
          </label>
          <label>Payload (JSON, required, ≤1 MB)
            <textarea id="ly-payload" rows="8" placeholder='{"type":"FeatureCollection","features":[]}'></textarea>
          </label>
          <div class="shuttle-form-actions">
            <button id="ly-save" class="shuttle-btn primary">Create</button>
          </div>
        </div>
      </div>
    </div>
  `;

	root.querySelectorAll<HTMLButtonElement>("[data-del]").forEach((btn) => {
		btn.addEventListener("click", async () => {
			const id = btn.dataset.del!;
			if (!confirm("Delete this layer?")) return;
			try {
				await api.deleteLayer(ctx.tenantSlug, id);
				await ctx.rerender();
			} catch (e) {
				alert((e as Error).message);
			}
		});
	});

	root.querySelector("#ly-save")?.addEventListener("click", async () => {
		const name = (
			root.querySelector("#ly-name") as HTMLInputElement
		).value.trim();
		const kind = (root.querySelector("#ly-kind") as HTMLSelectElement).value;
		const z =
			parseInt((root.querySelector("#ly-z") as HTMLInputElement).value, 10) ||
			0;
		const styleStr = (
			root.querySelector("#ly-style") as HTMLTextAreaElement
		).value.trim();
		const payloadStr = (
			root.querySelector("#ly-payload") as HTMLTextAreaElement
		).value.trim();
		if (!name || !payloadStr) {
			alert("Name and payload required");
			return;
		}
		let style: Record<string, unknown> | null = null;
		let payload: Record<string, unknown> | unknown[];
		try {
			if (styleStr) style = JSON.parse(styleStr);
			payload = JSON.parse(payloadStr);
		} catch (e) {
			alert(`JSON parse: ${(e as Error).message}`);
			return;
		}
		try {
			await api.createLayer(ctx.tenantSlug, {
				name,
				kind,
				style,
				payload,
				z_index: z,
			});
			await ctx.rerender();
		} catch (e) {
			alert((e as Error).message);
		}
	});
}

function escapeHtml(s: string): string {
	return s.replace(
		/[&<>"']/g,
		(c) =>
			({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
				c
			]!,
	);
}
